/**
 * InspectorProvider — developer-only inspection state layered on the race session (never on its path).
 *
 * Holds the backend inspection summary (motion verdicts, lineups, lap-line agreement, pit passages, finishes),
 * a windowed cache of raw provider samples with the motion model's verdicts, the overlay toggles, the inspector
 * zoom, and the frontend/backend sync check. Everything it draws comes from the engine's own outputs; nothing
 * here feeds back into playback, standings or the published state.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRaceSession } from "../RaceSessionProvider";
import { inspectorApi, SAMPLE } from "./inspectorApi";

const InspectorContext = createContext(null);

export const BUCKET_SECONDS = 30;
export const TRAIL_SECONDS = 20;
export const RAW_WINDOW_SECONDS = 2.5;
export const REJECTED_WINDOW_SECONDS = 30;
export const DEFAULT_TOGGLES = { raw: true, projected: true, path: true, lines: true, pit: true, trail: true, rejected: true, grid: true };

const ROW_FIELDS = ["position", "position_source", "timing_position", "laps_completed", "current_lap", "status", "status_note", "gap_to_leader_seconds", "gap_to_leader_laps", "gap_kind", "interval_seconds", "interval_laps", "interval_kind", "pit_count", "pit_window_s", "pit_lane_s", "finished_s", "grid_position", "is_lapped", "pending_penalty_seconds", "final"];
const HEADER_FIELDS = ["order", "timing_order", "order_source", "leader_laps", "total_laps", "classification_status"];
const CLOCK_FIELDS = ["session_seconds", "race_seconds", "race_start", "playback_time"];
const PHASE_FIELDS = ["phase", "race_start", "race_start_source", "race_start_confidence", "neutralized", "restarts", "restart_launches"];

function same(a, b) {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Field-by-field comparison of the browser's materialized state with the server's at the same t and build. */
export function compareSnapshots(client, server) {
    const diffs = [];
    let compared = 0;
    const check = (scope, key, a, b) => {
        compared += 1;
        if (!same(a, b)) diffs.push({ scope, key, client: a ?? null, server: b ?? null });
    };
    CLOCK_FIELDS.forEach((key) => check("clock", key, client?.clock?.[key], server?.clock?.[key]));
    PHASE_FIELDS.forEach((key) => check("phase", key, client?.phase?.[key], server?.phase?.[key]));
    HEADER_FIELDS.forEach((key) => check("header", key, client?.[key], server?.[key]));
    const serverRows = new Map((server?.entries || []).map((row) => [row.entry_id, row]));
    (client?.entries || []).forEach((row) => {
        const other = serverRows.get(row.entry_id);
        ROW_FIELDS.forEach((key) => check(row.acronym || row.entry_id, key, row[key], other?.[key]));
    });
    if ((client?.entries || []).length !== (server?.entries || []).length) diffs.push({ scope: "entries", key: "count", client: client?.entries?.length, server: server?.entries?.length });
    return { compared, diffs };
}

function lowerBound(rows, t) {
    let lo = 0;
    let hi = rows.length;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (rows[mid][SAMPLE.S] < t) lo = mid + 1;
        else hi = mid;
    }
    return lo;
}

export function InspectorProvider({ enabled, children }) {
    const session = useRaceSession();
    const { sessionKey, manifest, cursor, cursorRef, seekTo, pause, materializeAt, setSelectedEntry } = session;
    const [summary, setSummary] = useState(null);
    const [summaryStatus, setSummaryStatus] = useState("idle");
    const [toggles, setToggles] = useState(DEFAULT_TOGGLES);
    const [zoom, setZoom] = useState(null); // {preset, k, cx?, cy?} in the base projector's screen frame
    const [lineupIndex, setLineupIndex] = useState(null);
    const [sync, setSync] = useState(null);
    const [bucketsVersion, setBucketsVersion] = useState(0);
    const buckets = useRef(new Map()); // bucket index -> {entries} | Promise
    const buildRef = useRef(null);

    // Summary (the backend computes it once per build: ~10-20 s cold for a full race).
    useEffect(() => {
        if (!enabled || !sessionKey || !manifest?.build_id) return undefined;
        let cancelled = false;
        if (buildRef.current !== manifest.build_id) {
            buckets.current = new Map();
            buildRef.current = manifest.build_id;
        }
        setSummaryStatus("loading");
        inspectorApi
            .summary(sessionKey)
            .then((payload) => {
                if (cancelled) return;
                setSummary(payload);
                setSummaryStatus(payload.build_id === manifest.build_id ? "ready" : "build_mismatch");
                const startIndex = (payload.lineups || []).findIndex((lineup) => payload.start?.lineup_departed_s != null && lineup.departed_s === payload.start.lineup_departed_s);
                setLineupIndex(startIndex >= 0 ? startIndex : null);
            })
            .catch((error) => {
                if (!cancelled) setSummaryStatus(error?.status === 404 ? "disabled" : "error");
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, sessionKey, manifest?.build_id]);

    // Raw samples around the cursor, in 30 s buckets (trail + rejected window behind, interpolation partner ahead).
    const liveBucket = Math.floor(cursorRef.current.cursor / BUCKET_SECONDS);
    useEffect(() => {
        if (!enabled || !sessionKey || summaryStatus !== "ready") return;
        const now = cursorRef.current.cursor;
        const first = Math.floor((now - REJECTED_WINDOW_SECONDS) / BUCKET_SECONDS);
        const last = Math.floor((now + 2) / BUCKET_SECONDS) + 1;
        for (let index = first; index <= last; index += 1) {
            if (buckets.current.has(index)) continue;
            const promise = inspectorApi
                .samples(sessionKey, index * BUCKET_SECONDS, (index + 1) * BUCKET_SECONDS - 0.0005)
                .then((payload) => {
                    buckets.current.set(index, payload);
                    setBucketsVersion((value) => value + 1);
                })
                .catch(() => buckets.current.delete(index));
            buckets.current.set(index, promise);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, sessionKey, summaryStatus, cursor, liveBucket]);

    const samplesBetween = useCallback((entryId, t0, t1) => {
        const rows = [];
        for (let index = Math.floor(t0 / BUCKET_SECONDS); index <= Math.floor(t1 / BUCKET_SECONDS); index += 1) {
            const payload = buckets.current.get(index);
            const list = payload?.entries?.[entryId];
            if (!list) continue;
            for (let i = lowerBound(list, t0); i < list.length && list[i][SAMPLE.S] <= t1; i += 1) rows.push(list[i]);
        }
        return rows;
        // bucketsVersion invalidates consumers when a bucket arrives
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bucketsVersion]);

    const seekExact = useCallback(
        async (t) => {
            pause();
            await seekTo(Number(t));
        },
        [pause, seekTo]
    );

    const runSyncCheck = useCallback(async () => {
        pause();
        const t = cursorRef.current.cursor;
        const started = performance.now();
        try {
            const [client, server] = await Promise.all([materializeAt(t), inspectorApi.serverSnapshot(sessionKey, t)]);
            const result = compareSnapshots(client?.snapshot, server);
            const next = {
                t,
                at: new Date().toISOString(),
                clientBuild: manifest?.build_id,
                serverBuild: server?.source?.build_id,
                clientCheckpoint: client?.checkpoint,
                serverCheckpoint: server?.source?.checkpoint,
                serverPatches: server?.source?.patches_applied,
                ms: Math.round(performance.now() - started),
                ...result,
            };
            setSync(next);
            return next;
        } catch (error) {
            const next = { t, error: String(error?.message || error) };
            setSync(next);
            return next;
        }
    }, [pause, cursorRef, materializeAt, sessionKey, manifest?.build_id]);

    const value = useMemo(
        () => ({
            enabled,
            summary,
            summaryStatus,
            toggles,
            setToggle: (key, on) => setToggles((current) => ({ ...current, [key]: on ?? !current[key] })),
            setToggles,
            zoom,
            setZoom,
            lineupIndex,
            setLineupIndex,
            lineup: summary?.lineups && lineupIndex != null ? summary.lineups[lineupIndex] : null,
            samplesBetween,
            bucketsVersion,
            seekExact,
            sync,
            runSyncCheck,
            entriesById: new Map((summary?.entries || []).map((entry) => [entry.entry_id, entry])),
        }),
        [enabled, summary, summaryStatus, toggles, zoom, lineupIndex, samplesBetween, bucketsVersion, seekExact, sync, runSyncCheck]
    );

    // Scripted evidence capture (developer mode only): exact seeks, toggles, zoom, selection, sync check.
    useEffect(() => {
        if (!enabled || typeof window === "undefined") return undefined;
        window.__raceInspector = {
            ready: summaryStatus === "ready",
            status: summaryStatus,
            seekExact,
            setToggles: (next) => setToggles((current) => ({ ...current, ...next })),
            setZoom,
            select: (entryId) => setSelectedEntry(entryId),
            runSyncCheck,
            lastSync: sync,
            setLineupIndex,
        };
        return () => {
            delete window.__raceInspector;
        };
    }, [enabled, summaryStatus, seekExact, runSyncCheck, sync, setSelectedEntry]);

    return <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>;
}

export function useInspector() {
    const value = useContext(InspectorContext);
    return value && value.enabled ? value : null;
}
